import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { UserProfile, userAPI, supabase } from '../lib/supabaseClient';
import { useToast } from '../components/ToastContainer';
import i18n from '../i18n';

interface AuthContextType {
  user: UserProfile | null;
  isLoading: boolean;
  login: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  linkGoogleAccount: () => Promise<string>;
  refreshUserProfile: () => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<UserProfile>) => void;
  isAuthenticated: boolean;
  markAsExistingUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showError } = useToast();
  const isUpdatingRef = useRef(false);

  // 從 Supabase Auth 狀態更新用戶資料
  const updateUserFromAuth = async () => {
    if (isUpdatingRef.current) {
      console.log('⏭️ [AuthContext] updateUserFromAuth already in progress, skipping...');
      return;
    }
    isUpdatingRef.current = true;
    try {
      console.log('🔄 [AuthContext] updateUserFromAuth called');
      
      // 創建超時 Promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout')), 5000);
      });
      
      // 獲取會話的 Promise
      const sessionPromise = supabase.auth.getSession();
      
      // 使用 Promise.race 來添加超時保護
      const { data: { session }, error: sessionError } = await Promise.race([
        sessionPromise,
        timeoutPromise
      ]);
      
      if (sessionError) {
        console.error('❌ Error getting session:', sessionError);
        setUser(null);
        setIsLoading(false); // Set loading to false even on session error
        return;
      }
      
      if (!session || !session.user) {
        console.log('⚠️ [AuthContext] No active session found');
        setUser(null);
        setIsLoading(false); // Set loading to false when no session
        return;
      }
      
      console.log('✅ [AuthContext] Active session found for user:', session.user.id);
      
      // 🔥 OPTIMIZATION: Set loading to false immediately when we have a valid session
      // This allows the UI to render without waiting for the full profile fetch
      setIsLoading(false);
      
      // 從 profiles 表獲取完整的用戶資料，添加超時保護
      const profilePromise = userAPI.getProfile();
      const profileTimeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile query timeout')), 5000);
      });
      
      const userProfile = await Promise.race([
        profilePromise,
        profileTimeoutPromise
      ]);
      
      if (userProfile) {
        console.log('✅ [AuthContext] User profile loaded:', { 
          id: userProfile.id, 
          username: userProfile.username,
          wallet_address: userProfile.wallet_address,
          is_new_user: userProfile.is_new_user
        });
        setUser(userProfile);
      } else {
        console.log('⚠️ [AuthContext] No profile found for authenticated user, but session is valid');
        // 即使沒有 profile，也不要設置 user 為 null，因為可能是第一次登入
        setUser(null);
      }
      
    } catch (error) {
      if (error instanceof Error && error.message.includes('timeout')) {
        console.error('⏰ [AuthContext] Timeout while fetching user data:', error.message);
      } else {
        console.error('❌ [AuthContext] Error updating user from auth:', error);
        setUser(null);
      }
      setIsLoading(false);
    } finally {
      isUpdatingRef.current = false;
    }
  };

  // 監聽 Supabase Auth 狀態變化
  useEffect(() => {
    console.log('🔧 Setting up auth state listener...');
    
    // 設定超時保護，確保 isLoading 不會永遠卡住
    const loadingTimeout = setTimeout(() => {
      console.log('⏰ Loading timeout reached, forcing isLoading to false');
      setIsLoading(false);
    }, 10000); // 10秒超時
    
    // 初始狀態檢查
    updateUserFromAuth()
      .catch((error) => {
        console.error('❌ Error during initial auth check:', error);
        setUser(null);
      });
    
    // Clean up timeout when updateUserFromAuth completes (success or failure)
    // Note: isLoading is now managed inside updateUserFromAuth
    setTimeout(() => clearTimeout(loadingTimeout), 100);
    
    // 監聽認證狀態變化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 [AuthContext] Auth state changed:', event, session?.user?.id);
        
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('✅ [AuthContext] User signed in, updating profile...');
          try {
            await updateUserFromAuth();
          } catch (error) {
            console.error('❌ [AuthContext] Error updating user after sign in:', error);
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('🚨 [AuthContext] User signed out');
          setUser(null);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          console.log('🔄 [AuthContext] Token refreshed, updating profile...');
          try {
            await updateUserFromAuth();
          } catch (error) {
            console.error('❌ [AuthContext] Error updating user after token refresh:', error);
          }
        }
      }
    );

    return () => {
      console.log('🧹 [AuthContext] Cleaning up auth state listener');
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  // 添加日誌記錄用戶狀態變化
  useEffect(() => {
    if (user) {
      console.log('👤 [AuthContext] User state changed to:', {
        id: user.id,
        username: user.username || 'Unknown',
        wallet_address: user.wallet_address || 'None',
        world_id_sub: user.world_id_sub ? user.world_id_sub.slice(0, 8) + '...' : 'N/A',
        is_new_user: user.is_new_user
      });
    } else {
      console.log('👤 [AuthContext] User state changed: null');
    }
  }, [user]);

  // 添加 isLoading 狀態變化日誌
  useEffect(() => {
    console.log('⏳ [AuthContext] isLoading state changed to:', isLoading);
  }, [isLoading]);

  const login = async () => {
    try {
      console.log('🔐 [AuthContext] Login called - updating from auth state...');
      setIsLoading(true);

      // 讓 Supabase Auth 狀態監聽器處理用戶資料更新
      await updateUserFromAuth();

      console.log('✅ [AuthContext] Login process completed');

    } catch (error) {
      console.error('❌ [AuthContext] Login failed:', error);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      console.log('🔐 [AuthContext] Google OAuth login initiated...');
      setIsLoading(true);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        console.error('❌ [AuthContext] Google OAuth error:', error);
        setIsLoading(false);
        throw error;
      }

      console.log('✅ [AuthContext] Google OAuth redirect initiated');
      // Note: 頁面會重定向，所以不需要在這裡處理後續邏輯

    } catch (error) {
      console.error('❌ [AuthContext] Google login failed:', error);
      setIsLoading(false);
      showError('Google 登入失敗', error instanceof Error ? error.message : '未知錯誤');
      throw error;
    }
  };

  const linkGoogleAccount = async (): Promise<string> => {
    try {
      console.log('🔗 [AuthContext] Linking Google account via Edge Function...');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('未登入，無法連結 Gmail');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${supabaseUrl}/functions/v1/create-link-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await resp.json();

      if (!resp.ok) {
        console.error('❌ [AuthContext] create-link-token failed:', data);
        throw new Error(data.message ?? '無法建立連結請求，請稍後再試');
      }

      const token: string = data.token;
      sessionStorage.setItem('pendingLinkToken', token);
      console.log('✅ [AuthContext] Link token received and stored');

      const linkUrl = `${window.location.origin}/link-google?token=${token}`;
      console.log('✅ [AuthContext] Link URL generated:', linkUrl);
      return linkUrl;

    } catch (error) {
      console.error('❌ [AuthContext] Link Google account failed:', error);
      showError('連結 Gmail 失敗', error instanceof Error ? error.message : '未知錯誤');
      throw error;
    }
  };

  const refreshUserProfile = async () => {
    try {
      console.log('🔄 [AuthContext] Refreshing user profile...');
      const userProfile = await userAPI.getProfile();
      if (userProfile) {
        setUser(userProfile);
        console.log('✅ [AuthContext] User profile refreshed');
      }
    } catch (error) {
      console.error('❌ [AuthContext] Failed to refresh user profile:', error);
    }
  };

  const logout = async () => {
    try {
      console.log('🚨 Logging out user...');
      
      // 登出 Supabase Auth
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('❌ Error during logout:', error);
        throw error;
      }
      
      console.log('✅ User logged out successfully');
      setUser(null);
      
    } catch (error) {
      console.error('❌ Error during logout:', error);
      showError('登出錯誤', error instanceof Error ? error.message : '未知錯誤');
    }
  };

  const updateUser = (updates: Partial<UserProfile>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      console.log('🔄 User updated in context:', updatedUser);
    } else {
      console.log('⚠️ Cannot update user - no user in context');
    }
  };

  const markAsExistingUser = async () => {
    try {
      console.log('🎯 Marking user as existing user...');
      const updatedUser = await userAPI.markAsExistingUser();
      setUser(updatedUser);
      console.log('✅ User marked as existing user');
    } catch (error) {
      console.error('❌ Error marking user as existing:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    loginWithGoogle,
    linkGoogleAccount,
    refreshUserProfile,
    logout,
    updateUser,
    isAuthenticated: !!user,
    markAsExistingUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};